import Papa from 'papaparse';

import type { CatalogTemplateRow, VitalElement, WizardState } from './types';

/**
 * CSV column names that match the upload format expected by UploadMarkdownsDialog.
 * yadjusted_code  = override reason code
 * yadjusted_price = override price (in cents)
 */
const CSV_COLUMNS = [
  'parent_sku',
  'sku_subcode',
  'color',
  'size_code',
  'yadjusted_code',
  'yadjusted_price',
] as const;

/**
 * Builds a deduplication key from a catalog row based on which vital elements
 * the user selected. Rows sharing the same key are collapsed into one template row.
 */
export function deduplicationKey(row: CatalogTemplateRow, elements: VitalElement[]): string {
  const parts: string[] = [row.parentSku ?? ''];
  if (elements.includes('colorCode')) parts.push(row.colorCode ?? '');
  if (elements.includes('sizeCode')) parts.push(row.sizeCode ?? '');
  return parts.join('\x00');
}

function deduplicateRows(
  rows: CatalogTemplateRow[],
  elements: VitalElement[],
): CatalogTemplateRow[] {
  const seen = new Map<string, CatalogTemplateRow>();
  for (const row of rows) {
    const key = deduplicationKey(row, elements);
    if (!seen.has(key)) {
      seen.set(key, row);
    }
  }
  return Array.from(seen.values());
}

export interface AmbiguousPricingEntry {
  key: string;
  parentSku: string;
  distinctPrices: number[];
}

/**
 * Detects deduplication groups where multiple distinct yerdlePrice values exist.
 * These groups would produce inaccurate prices in the generated template because
 * only the first row's price is kept during deduplication.
 */
export function detectAmbiguousPricing(
  rows: CatalogTemplateRow[],
  elements: VitalElement[],
): AmbiguousPricingEntry[] {
  const groups = new Map<string, { parentSku: string, prices: Set<number> }>();

  for (const row of rows) {
    const key = deduplicationKey(row, elements);
    let group = groups.get(key);
    if (!group) {
      group = { parentSku: row.parentSku ?? '', prices: new Set() };
      groups.set(key, group);
    }
    if (row.yerdlePrice != null) {
      group.prices.add(row.yerdlePrice);
    }
  }

  const result: AmbiguousPricingEntry[] = [];
  for (const [key, group] of groups) {
    if (group.prices.size > 1) {
      result.push({
        key,
        parentSku: group.parentSku,
        distinctPrices: Array.from(group.prices).sort((a, b) => a - b),
      });
    }
  }
  return result;
}

const ALL_VITAL_ELEMENTS: VitalElement[] = ['parentSku', 'colorCode', 'sizeCode'];

export interface ResolvingElementsSuggestion {
  /** Elements to suggest adding. */
  elements: VitalElement[];
  /** If true, all of `elements` must be added together; if false, any one of them alone would resolve it. */
  requiresAll: boolean;
}

/**
 * Suggests which vital elements should be added to resolve pricing ambiguity.
 * If any single missing element would resolve it on its own, returns those with
 * `requiresAll: false`. Otherwise, if all missing elements together would resolve
 * it, returns those with `requiresAll: true`. Returns an empty `elements` array if
 * ambiguity cannot be resolved by any combination.
 */
export function suggestResolvingElements(
  rows: CatalogTemplateRow[],
  currentElements: VitalElement[],
): ResolvingElementsSuggestion {
  const missing = ALL_VITAL_ELEMENTS.filter(e => !currentElements.includes(e));

  // Check which single additions would resolve all ambiguity
  const resolving: VitalElement[] = [];
  for (const candidate of missing) {
    const withCandidate = [...currentElements, candidate];
    if (detectAmbiguousPricing(rows, withCandidate).length === 0) {
      resolving.push(candidate);
    }
  }

  if (resolving.length > 0) return { elements: resolving, requiresAll: false };

  // No single addition resolves it — check if all elements together resolve it
  if (missing.length > 0 && detectAmbiguousPricing(rows, ALL_VITAL_ELEMENTS).length === 0) {
    return { elements: missing, requiresAll: true }; // Need all missing elements
  }

  return { elements: [], requiresAll: false }; // Unresolvable
}

function computePrice(row: CatalogTemplateRow, state: WizardState): string {
  switch (state.pricingMode) {
    case 'blank':
      return '';
    case 'resalePrice':
      return row.yerdlePrice != null ? String(row.yerdlePrice) : '';
    case 'percentOff':
      if (row.yerdlePrice == null) return '';
      return String(Math.round(row.yerdlePrice * (1 - state.percentOff / 100)));
  }
}

export interface TemplateRow {
  parent_sku: string;
  sku_subcode: string;
  color: string;
  size_code: string;
  yadjusted_code: string;
  yadjusted_price: string;
  /** Display-only in the review table; not included in the CSV. */
  color_code: string;
}

/**
 * Builds template rows from catalog data + wizard state.
 * This is split from CSV generation so the Review step can display the same rows
 * in a table before the user downloads.
 */
export function buildTemplateRows(
  catalogRows: CatalogTemplateRow[],
  state: WizardState,
): TemplateRow[] {
  const deduped = deduplicateRows(catalogRows, state.selectedVitalElements);
  const hasColor = state.selectedVitalElements.includes('colorCode');
  const hasSize = state.selectedVitalElements.includes('sizeCode');

  return deduped.map(row => ({
    parent_sku: row.parentSku ?? '',
    sku_subcode: hasColor || hasSize ? (row.skuSubcode ?? '') : '',
    color: hasColor ? (row.color ?? '') : '',
    size_code: hasSize ? (row.sizeCode ?? '') : '',
    yadjusted_code: state.reasonCode,
    yadjusted_price: computePrice(row, state),
    color_code: hasColor ? (row.colorCode ?? '') : '',
  }));
}

/**
 * Generates a CSV string ready for download. The column names match
 * the format expected by the "Upload Override Prices" upload flow.
 */
export function generateTemplateCsv(
  catalogRows: CatalogTemplateRow[],
  state: WizardState,
): string {
  const rows = buildTemplateRows(catalogRows, state);
  return Papa.unparse(rows, { columns: CSV_COLUMNS as unknown as string[] });
}
